import { DuplicateIcon } from '@heroicons/react/outline';
import { FaLine } from 'react-icons/fa6';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import { useChannelsT } from '../useChannelsT';
import { CheckGlyph, delay, tone } from '../../primitives';

/**
 * The two keys from the LINE Developers console, and the agent's LINE window
 * once they're pasted: checked, with the webhook registered for you.
 */
export default function LineSetup() {
  const { t } = useChannelsT();

  return (
    <div className='relative mx-auto max-w-[31rem] pb-2 sm:pb-28'>
      <div className='overflow-hidden rounded-[22px] bg-white text-start text-[#111] shadow-[0_40px_90px_-35px_rgba(30,20,90,0.55)] ring-1 ring-black/5 dark:bg-[#1f2329] dark:text-[#eef1f5] dark:ring-white/10'>
        <div className='flex items-center gap-2 bg-[#06C755] px-5 py-3 text-white'>
          <FaLine className='h-5 w-5' />
          <span className='text-sm font-semibold'>LINE Developers</span>
        </div>
        <dl className='space-y-4 px-5 py-5 text-[13px]'>
          <div>
            <dt className='text-[#6b7280] dark:text-[#9aa3ad]'>
              <span className='font-semibold text-[#111] dark:text-white'>
                {t('Basic settings')}
              </span>
              {' · '}
              {t('Channel secret')}
            </dt>
            <dd className='mt-1.5 flex items-center gap-3 rounded-lg bg-[#f4f5f7] px-3 py-2 font-mono text-[12px] dark:bg-white/[0.06]'>
              <span className='min-w-0 flex-1 truncate tracking-[0.2em]'>
                ••••••••••••••••
              </span>
              <DuplicateIcon className='h-4 w-4 flex-none text-[#6b7280]' />
            </dd>
          </div>
          <div>
            <dt className='text-[#6b7280] dark:text-[#9aa3ad]'>
              <span className='font-semibold text-[#111] dark:text-white'>
                {t('Messaging API')}
              </span>
              {' · '}
              {t('Channel access token (long-lived)')}
            </dt>
            <dd className='mt-1.5 flex items-center gap-3 rounded-lg bg-[#f4f5f7] px-3 py-2 font-mono text-[12px] dark:bg-white/[0.06]'>
              <span className='min-w-0 flex-1 truncate tracking-[0.2em]'>
                ••••••••••••••••••••••
              </span>
              <span className='flex-none rounded-md bg-[#06C755] px-2.5 py-1 font-sans text-[11px] font-semibold text-white'>
                {t('Issue')}
              </span>
            </dd>
          </div>
        </dl>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 rounded-2xl p-4 text-start shadow-[0_30px_60px_-28px_rgba(30,20,90,0.6)] sm:absolute sm:-bottom-2 sm:end-[-1.5rem] sm:mt-0 sm:w-[20rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(450)}
      >
        <p
          className={clsxm(
            'flex items-center gap-2 text-[14px] font-semibold',
            tone.ink,
          )}
        >
          <FaLine className='h-4 w-4 text-[#06C755]' />
          {t('LINE integration')}
        </p>
        <ul className={clsxm('mt-3 space-y-2 text-[12.5px]', tone.body)}>
          {[
            t('Channel access token'),
            t('Channel secret'),
            t('Webhook registered with LINE'),
          ].map((item) => (
            <li key={item} className='flex items-center gap-2'>
              <span className='flex h-4 w-4 flex-none items-center justify-center rounded-full bg-emerald-500 text-white'>
                <CheckGlyph className='h-2.5 w-2.5' />
              </span>
              {item}
            </li>
          ))}
        </ul>
        <div className='mt-3 flex items-center justify-between gap-3'>
          <span className={clsxm('flex items-center gap-2 text-xs', tone.body)}>
            <span className='relative h-5 w-9 flex-none rounded-full bg-primary-600'>
              <span className='absolute end-0.5 top-0.5 h-4 w-4 rounded-full bg-white' />
            </span>
            {t('Enable integration')}
          </span>
          <span className='flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
            <CheckGlyph className='h-3.5 w-3.5' />
            {t('Connected')}
          </span>
        </div>
      </div>
    </div>
  );
}
